/*
 * PDP mobile layout behavior for sections/main-product-2.liquid.
 * <pdp-read-more> collapses the hero description on mobile.
 * <pdp-trust-line> swaps its message with the selected purchase option.
 * <pdp-sticky-buy-bar> proxies the main add to cart button in a fixed bar.
 */

// Leaves room for the sticky site header when scrolling to the buy box
const BUY_BOX_SCROLL_OFFSET = 80;

class PdpReadMore extends HTMLElement {
  connectedCallback () {
    this.container = this.closest('[data-role]');
    this.button = this.querySelector('.pdp-read-more__button');
    this.content = this.container ? this.container.querySelector('.productView-customText') : null;

    if (!this.container || !this.button || !this.content) {
      return;
    }

    this.handleClick = this.toggle.bind(this);
    this.button.addEventListener('click', this.handleClick);

    this.resizeObserver = new ResizeObserver(() => {
      this.update();
    });
    this.resizeObserver.observe(this.content);

    // The hero description is swapped per variant, so re-measure on content change
    this.mutationObserver = new MutationObserver(() => {
      this.update();
    });
    this.mutationObserver.observe(this.content, { childList: true, subtree: true, characterData: true });

    this.update();
  }

  disconnectedCallback () {
    if (this.button && this.handleClick) {
      this.button.removeEventListener('click', this.handleClick);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  update () {
    if (this.container.classList.contains('is-expanded')) {
      return;
    }

    const isOverflowing = this.content.scrollHeight > this.content.clientHeight + 1;

    this.container.classList.toggle('is-short', !isOverflowing);
    this.button.hidden = !isOverflowing;
  }

  toggle () {
    const isExpanded = this.container.classList.toggle('is-expanded');

    this.button.setAttribute('aria-expanded', String(isExpanded));

    if (!isExpanded) {
      this.update();
    }
  }
}

class PdpStickyBuyBar extends HTMLElement {
  connectedCallback () {
    this.stickyButton = this.querySelector('[data-pdp-sticky-buy-button]');
    this.mainButton = document.getElementById('product-add-to-cart');

    if (!this.stickyButton || !this.mainButton) {
      return;
    }

    const productSection = this.mainButton.closest('[id^="ProductSection-"]') || document;

    this.buyBox = productSection.querySelector('.productView-sub-picker');
    this.buyButtons = this.mainButton.closest('[data-pdp-buy-buttons]') || this.mainButton;
    this.watchedElements = [this.buyBox, this.buyButtons].filter(Boolean);
    this.elementsOnScreen = new Set();
    this.isBuyBoxBelowViewport = true;

    this.handleClick = this.onStickyButtonClick.bind(this);
    this.stickyButton.addEventListener('click', this.handleClick);

    // Mirror label, price and disabled state set by the subscription picker and variant scripts
    this.mainButtonObserver = new MutationObserver(() => {
      this.syncButton();
    });
    this.mainButtonObserver.observe(this.mainButton, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    // Only count the buy box as on screen once it reaches the upper 75% of the viewport,
    // so a sliver peeking in at the bottom does not hide the bar
    this.intersectionObserver = new IntersectionObserver(this.onIntersection.bind(this), {
      rootMargin: '0px 0px -25% 0px'
    });
    this.watchedElements.forEach((element) => {
      this.intersectionObserver.observe(element);
    });

    document.body.classList.add('has-pdp-sticky-buy-bar');
    this.syncButton();
  }

  disconnectedCallback () {
    if (this.stickyButton && this.handleClick) {
      this.stickyButton.removeEventListener('click', this.handleClick);
    }

    if (this.mainButtonObserver) {
      this.mainButtonObserver.disconnect();
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    document.body.classList.remove('has-pdp-sticky-buy-bar');
  }

  syncButton () {
    this.stickyButton.innerHTML = this.mainButton.innerHTML;
    this.stickyButton.disabled = this.mainButton.disabled;
  }

  onIntersection (entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.elementsOnScreen.add(entry.target);
      } else {
        this.elementsOnScreen.delete(entry.target);
      }

      if (entry.target === this.watchedElements[0]) {
        this.isBuyBoxBelowViewport = !entry.isIntersecting && entry.boundingClientRect.top > 0;
      }
    });

    this.setVisible(this.elementsOnScreen.size === 0);
  }

  setVisible (isVisible) {
    this.classList.toggle('is-visible', isVisible);
    this.inert = !isVisible;
  }

  onStickyButtonClick () {
    if (this.mainButton.disabled) {
      return;
    }

    // Until the shopper has reached the buy box, show it first so the purchase
    // option (subscription or one-time) is a visible, deliberate choice
    if (this.isBuyBoxBelowViewport && this.buyBox) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const firstControl = this.buyBox.querySelector('input:not([type="hidden"]), select, button');

      const buyBoxTop = this.buyBox.getBoundingClientRect().top + window.scrollY - BUY_BOX_SCROLL_OFFSET;

      window.scrollTo({ top: buyBoxTop, behavior: prefersReducedMotion ? 'auto' : 'smooth' });

      if (firstControl) {
        firstControl.focus({ preventScroll: true });
      }

      return;
    }

    this.mainButton.click();
  }
}

class PdpTrustLine extends HTMLElement {
  connectedCallback () {
    this.textElement = this.querySelector('[data-pdp-trust-line-text]');

    if (!this.textElement) {
      return;
    }

    this.handleOptionChange = (event) => {
      this.render(event.detail ? event.detail.option : null);
    };
    document.addEventListener('subscription-picker:option-change', this.handleOptionChange);

    const selectedOption = document.querySelector('.purchase-option.is-selected');

    this.render(selectedOption ? selectedOption.dataset.option : null);
  }

  disconnectedCallback () {
    if (this.handleOptionChange) {
      document.removeEventListener('subscription-picker:option-change', this.handleOptionChange);
    }
  }

  render (option) {
    const message = option === 'onetime' ? this.dataset.textOnetime : this.dataset.textSubscribe;

    const parts = (message || '').split('·').map((part) => part.trim()).filter(Boolean);

    this.textElement.replaceChildren();

    // Each · becomes a spaced separator element, the font renders plain spaces too tight
    parts.forEach((part, index) => {
      if (index > 0) {
        const separator = document.createElement('span');

        separator.className = 'pdp-trust-line__separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = '·';
        this.textElement.append(separator);
      }

      this.textElement.append(document.createTextNode(part));
    });

    this.hidden = parts.length === 0;
  }
}

if (!customElements.get('pdp-read-more')) {
  customElements.define('pdp-read-more', PdpReadMore);
}

if (!customElements.get('pdp-trust-line')) {
  customElements.define('pdp-trust-line', PdpTrustLine);
}

if (!customElements.get('pdp-sticky-buy-bar')) {
  customElements.define('pdp-sticky-buy-bar', PdpStickyBuyBar);
}
